import { useEffect, useState } from 'react'
import validator from 'validator'
import { api } from 'core/api/ConnectionManager'
import translate from 'core/translate'
import { isExist } from 'core/utils/coreUtil'
import { IUser, IUserTokens } from 'core/models/Users'
import request from 'superagent'
import { useLocation } from 'react-router'
import { emitError, emitAppError } from 'features/appNotifications/AppNotifications.state'
import { useDispatch } from 'react-redux'
import { login as loginAction } from 'blocks.app/user/user.state'
import { headActions } from 'features/head/Head.state'
import { getDefaultLocale } from '../helpers/user'
import {
    SecondFactorLoginMethod,
    TwoFactorChallenge,
    resolveAvailableMethods,
    resolvePreferredMethod,
} from 'core/models/SecondFactor'

export type TAuthorizationLoginStepTypes = 'ONE' | 'TWO'

export interface IDefaultLoginData {
    user: IUser
    tokens: IUserTokens
}

export interface ITwoFactorLoginData extends TwoFactorChallenge {
    prefix?: string
    timeoutS?: number
}

export interface IUseAuthorizationLogin {
    authStep: TAuthorizationLoginStepTypes
    email: string
    password: string
    code: string
    prefix: string | null
    secondFactorMethod: SecondFactorLoginMethod
    availableMethods: SecondFactorLoginMethod[]
    isLoading: boolean
    minAuthTimer: number
    secAuthTimer: number
    authTimerChanged: boolean
    onChangeEmail: (email: string) => void
    onChangePassword: (password: string) => void
    onChangeCode: (code: string) => void
    onChangeSecondFactorMethod: (method: SecondFactorLoginMethod) => void
    login: (dataTokens?: {
        tokens: { accessToken: string; refreshToken?: string }
        isYandexAuth?: boolean
        isGoogleAuth?: boolean
    }) => void
    confirmCode: (codeOverride?: string) => void
    errorMessage: string
    setErrorMessage: (message: string) => void
}

export interface IUseAuthorizationLoginProps {
    onLoading?: (loading: boolean) => void
    accessToken?: string
    onLogin?: (user: IUser) => void
}

export interface ILoginFirstStepRequest {
    email: string
    password: string
    addAccountTo?: string
    restoreAccess?: boolean
}

export interface ILoginSecondStepRequest {
    code: string
    preToken: string
    method?: SecondFactorLoginMethod
}

export const useAuthorizationLogin = ({ onLoading, onLogin, accessToken }: IUseAuthorizationLoginProps) => {
    const dispatch = useDispatch()
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [code, setCode] = useState<string>('')
    const [attempts, setAttempts] = useState<number | null>(null)
    const [timeoutS, setTimeoutS] = useState<number | null>(null)
    const [prefix, setPrefix] = useState<string | null>(null)
    const [preToken, setPreToken] = useState<string | null>(null)
    const [availableMethods, setAvailableMethods] = useState<SecondFactorLoginMethod[]>([])
    const [secondFactorMethod, setSecondFactorMethod] = useState<SecondFactorLoginMethod>('email')
    const [authStep, setAuthStep] = useState<TAuthorizationLoginStepTypes>('ONE')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [secAuthTimer, setSecAuthTimer] = useState<number>(0)
    const [minAuthTimer, setMinAuthTimer] = useState<number>(0)
    const [authTimerChanged, setAuthTimerChanged] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [emptyLogin, setEmptyLogin] = useState<boolean>(false)
    const [recoverAccess, setRecoverAccess] = useState<boolean>(false)
    const location = useLocation()

    useEffect(() => {
        if (location.pathname !== '/settings') {
            dispatch(headActions.setTitle([translate('login')]))
        }
    }, [])

    useEffect(() => {
        if (!timeoutS) return

        const timer = setTimeout(() => {
            clearSendCodeParams()
            clearUserData()
            dispatch(emitError('confirmEmailTimeoutError'))
        }, timeoutS * 1000)
        return () => {
            clearTimeout(timer)
        }
    }, [timeoutS])

    const clearSendCodeParams = () => {
        setAttempts(null)
        setPrefix(null)
        setTimeoutS(null)
        setPreToken(null)
        setAvailableMethods([])
        setSecondFactorMethod('email')
        setAuthStep('ONE')
    }

    const clearUserData = () => {
        setPassword('')
        setEmail('')
        setCode('')
    }

    const onChangeSecondFactorMethod = (method: SecondFactorLoginMethod) => {
        setSecondFactorMethod(method)
        setCode('')
    }

    const onChangeEmail = (email: string) => {
        if (email.includes('@') && emptyLogin) {
            return
        }
        setEmail(email.trim())
    }

    useEffect(() => {
        if (email.length === 0) {
            setEmptyLogin(true)
        }
    }, [email])

    useEffect(() => {
        if (emptyLogin) {
            setTimeout(() => {
                setEmptyLogin(false)
            }, 100)
        }
    }, [emptyLogin])

    const onChangePassword = (password: string) => {
        setPassword(password)
    }

    const onChangeCode = (code: string) => {
        setCode(code)
    }

    const onLoadingCb = (value: boolean) => {
        if (!onLoading) return

        onLoading(value)
    }

    const validate = () => {
        if (!email) {
            dispatch(emitError('emailEmpty'))
            return false
        }
        if (!password) {
            dispatch(emitError('passwordEmpty'))
            return false
        }
        if (!validator.isEmail(email)) {
            dispatch(emitError('emailFormatError'))
            return false
        }

        return true
    }

    const onTwoFactorLoginSuccess = (data: ITwoFactorLoginData) => {
        if (!data) {
            return
        }

        const methods = resolveAvailableMethods(data)
        const preferred = resolvePreferredMethod(data, methods)

        setAttempts(data.attempts)
        setPreToken(data.preToken)
        setTimeoutS(data.timeoutS ?? null)
        setPrefix(data.prefix ?? null)
        setAvailableMethods(methods)
        setSecondFactorMethod(preferred)
        setCode('')
        setAuthStep('TWO')
    }

    const onDefaultLoginSuccess = (data: IDefaultLoginData) => {
        if (onLogin) {
            onLogin(data.user)
        } else {
            dispatch(
                loginAction({
                    tokens: data.tokens,
                    user: data.user,
                })
            )
        }
    }

    const onLoginError = (response: any) => {
        let errorMessage = 'serverIsNotAvailable'

        if (response && response.body && response.body.error === 80) {
            setMinAuthTimer(response.body.data.blockedFor.min)
            setSecAuthTimer(response.body.data.blockedFor.sec)
            setAuthTimerChanged(!authTimerChanged)
        }
        if (response) {
            errorMessage = response.body.error
        }

        if ([18, 22].includes(response?.body?.error)) {
            setErrorMessage(translate(response.body.error))
        }

        dispatch(emitError(errorMessage))
    }

    const onConfirmCodeError = (response: any) => {
        let errorMessage = 'serverIsNotAvailable'
        let additionMessage = ''

        if (response) {
            errorMessage = response.body.error
            let data = response.body.data

            if (data && data.attempts > 0) {
                setAttempts(data.attempts)
                additionMessage = `${data.attempts}`
            }

            if (data && data.attempts === 0) {
                clearSendCodeParams()
                clearUserData()
                additionMessage = '0'
            }
        }

        dispatch(
            emitError({
                error: errorMessage,
                message: additionMessage,
            })
        )
    }

    const confirmCode = (codeOverride?: string) => {
        const codeToSend = codeOverride ?? code
        if (!isExist(codeToSend) || !preToken || !isExist(attempts) || attempts === 0) return

        if (codeOverride) {
            setCode(codeOverride)
        }

        onLoadingCb(true)
        setIsLoading(true)

        const payload: ILoginSecondStepRequest = {
            code: codeToSend,
            preToken,
            method: secondFactorMethod,
        }

        api.post<ILoginSecondStepRequest>('login', payload)
            .then((res: request.Response) => {
                onLoadingCb(false)
                setIsLoading(false)
                clearSendCodeParams()
                clearUserData()

                let data: IDefaultLoginData = res.body.data
                onDefaultLoginSuccess(data)
            })
            .catch(({ response }: { response: any }) => {
                onLoadingCb(false)
                setIsLoading(false)
                onConfirmCodeError(response)
            })
    }

    const login = (dataTokens?: {
        tokens: { accessToken: string; refreshToken?: string }
        isYandexAuth?: boolean
        isGoogleAuth?: boolean
    }) => {
        if (dataTokens && dataTokens.isGoogleAuth && !dataTokens.isYandexAuth) {
            onLoadingCb(true)
            setIsLoading(true)

            const accessToken: string = dataTokens.tokens.accessToken

            fetch(window.spconfig.ip_address + 'user/auth/google', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({
                    accessToken,
                    expireD: window.spconfig.registration.expireD,
                    locale: getDefaultLocale(),
                }),
            })
                .then((res) => res.json())
                .then((json) => {
                    onLoadingCb(false)
                    setIsLoading(false)
                    const data = {
                        tokens: json.data.tokens,
                        user: json.data.user,
                    }
                    onDefaultLoginSuccess(data as IDefaultLoginData)
                })
                .catch(({ response }: { response: any }) => {
                    onLoadingCb(false)
                    setIsLoading(false)
                    onLoginError(response)
                })

            clearQueryString()

            return
        }

        if (dataTokens && dataTokens.isYandexAuth && !dataTokens.isGoogleAuth) {
            onLoadingCb(true)
            setIsLoading(true)

            const accessToken: string = dataTokens.tokens.accessToken

            fetch(window.spconfig.ip_address + 'user/auth/yandex', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({
                    accessToken,
                    expireD: window.spconfig.registration.expireD,
                    locale: getDefaultLocale(),
                }),
            })
                .then((res) => res.json())
                .then((json) => {
                    onLoadingCb(false)
                    setIsLoading(false)
                    const data = {
                        tokens: json.data.tokens,
                        user: json.data.user,
                    }
                    onDefaultLoginSuccess(data as IDefaultLoginData)
                })
                .catch(({ response }: { response: any }) => {
                    onLoadingCb(false)
                    setIsLoading(false)
                    onLoginError(response)
                })

            clearQueryString()

            return
        }

        if (dataTokens && !dataTokens.isYandexAuth && !dataTokens.isGoogleAuth) {
            const tokens = dataTokens.tokens

            fetch(window.spconfig.ip_address + 'user', {
                method: 'GET',
                headers: new Headers({
                    Authorization: `Bearer ${tokens.accessToken}`,
                }),
            })
                .then((res) => res.json())
                .then((json) => {
                    const data = {
                        tokens: tokens,
                        user: json.data,
                    }

                    onDefaultLoginSuccess(data as IDefaultLoginData)
                })

            clearQueryString()

            return
        }

        if (!validate()) {
            return
        }

        onLoadingCb(true)
        setIsLoading(true)

        let loginParams: ILoginFirstStepRequest = { email, password }

        if (accessToken) {
            loginParams.addAccountTo = accessToken
        }

        if (recoverAccess) {
            loginParams.restoreAccess = true
        }

        api.post<ILoginFirstStepRequest>('login', loginParams)
            .then((res: request.Response) => {
                onLoadingCb(false)
                setIsLoading(false)

                let data: ITwoFactorLoginData | (IDefaultLoginData & { preToken?: null }) = res.body.data

                if (data.preToken) {
                    onTwoFactorLoginSuccess(data as ITwoFactorLoginData)
                } else {
                    onDefaultLoginSuccess(data as IDefaultLoginData)
                    clearUserData()
                }

                if (recoverAccess) {
                    setRecoverAccess(false)
                }
            })
            .catch(({ response }: { response: any }) => {
                onLoadingCb(false)
                setIsLoading(false)

                if (response?.body?.error === 124) {
                    setRecoverAccess(false)
                    dispatch(emitError('restoreAccessUserNotRoot'))

                    return
                }

                if (response && response.body && response.body.data && response.body.data.isAccountRecoverable) {
                    setRecoverAccess(true)

                    return
                }

                onLoginError(response)
            })
    }
    function clearQueryString() {
        const location = window.location
        const params = new URLSearchParams(location.search)
        params.delete('success')
        params.delete('result')
        window.history.replaceState(null, '', '?' + params + '#' + location.hash)
    }

    const resetRecoveryMode = () => {
        setRecoverAccess(false)
    }

    return {
        password,
        email,
        login,
        code,
        prefix,
        secondFactorMethod,
        availableMethods,
        confirmCode,
        onChangeEmail,
        onChangePassword,
        onChangeCode,
        onChangeSecondFactorMethod,
        authStep,
        isLoading,
        secAuthTimer,
        minAuthTimer,
        authTimerChanged,
        errorMessage,
        setErrorMessage,
        recoverAccess,
        resetRecoveryMode,
    }
}
